import { useState, useRef, useEffect } from "react";
import MainLayout from "@/components/feature/MainLayout";

interface Call {
  id: string;
  caller: string;
  phone: string;
  userId: string;
  type: string;
  status: "active" | "waiting" | "ended" | "missed";
  duration: number;
  startTime: string;
  agent: string;
  sentiment: "positive" | "neutral" | "negative";
  tags: string[];
}

interface Message {
  role: "user" | "ai" | "system";
  content: string;
  time: string;
}

interface Ticket {
  id: string;
  title: string;
  type: string;
  priority: "urgent" | "high" | "medium" | "low";
  status: "open" | "processing" | "pending" | "resolved" | "closed";
  channel: string;
  channelIcon: string;
  user: string;
  userId: string;
  phone: string;
  createTime: string;
  updateTime: string;
  agent: string;
  description: string;
  satisfaction?: number;
  tags: string[];
}

interface Channel {
  id: string;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  status: "online" | "offline" | "maintenance";
  todayCount: number;
  avgResponse: string;
  connected: boolean;
}

const mockCalls: Call[] = [
  { id: "CALL-001", caller: "张建国", phone: "138****5678", userId: "R001", type: "账单咨询", status: "active", duration: 245, startTime: "14:22", agent: "客服01", sentiment: "neutral", tags: ["账单", "缴费"] },
  { id: "CALL-002", caller: "李秀梅", phone: "139****1234", userId: "R002", type: "泄漏报修", status: "waiting", duration: 0, startTime: "14:28", agent: "待接入", sentiment: "negative", tags: ["紧急", "泄漏"] },
  { id: "CALL-003", caller: "王大明", phone: "137****9876", userId: "R003", type: "报装咨询", status: "ended", duration: 380, startTime: "13:45", agent: "客服02", sentiment: "positive", tags: ["报装", "新用户"] },
  { id: "CALL-004", caller: "陈小红", phone: "136****5432", userId: "R004", type: "安检预约", status: "ended", duration: 120, startTime: "13:20", agent: "客服01", sentiment: "positive", tags: ["安检", "预约"] },
  { id: "CALL-005", caller: "未知来电", phone: "150****8888", userId: "-", type: "未分类", status: "missed", duration: 0, startTime: "13:10", agent: "-", sentiment: "neutral", tags: [] },
];

const mockTickets: Ticket[] = [
  { id: "TK-2026-0892", title: "燃气表读数异常，疑似计量错误", type: "设备问题", priority: "high", status: "processing", channel: "微信公众号", channelIcon: "ri-wechat-line", user: "张建国", userId: "R001", phone: "138****5678", createTime: "2026-04-10 09:15", updateTime: "2026-04-10 10:30", agent: "客服01", description: "用户反映本月燃气表读数比往常高出约30%，怀疑计量设备存在问题，要求上门检查。", satisfaction: undefined, tags: ["计量", "设备"] },
  { id: "TK-2026-0891", title: "申请更换老旧燃气软管", type: "维修申请", priority: "medium", status: "pending", channel: "APP", channelIcon: "ri-smartphone-line", user: "李秀梅", userId: "R002", phone: "139****1234", createTime: "2026-04-10 08:45", updateTime: "2026-04-10 09:00", agent: "待分配", description: "用户家中燃气软管使用超过5年，出现老化迹象，申请免费更换。", satisfaction: undefined, tags: ["软管", "更换"] },
  { id: "TK-2026-0890", title: "账单金额与实际用气量不符", type: "账单争议", priority: "high", status: "open", channel: "飞书", channelIcon: "ri-message-2-line", user: "王大明", userId: "R003", phone: "137****9876", createTime: "2026-04-09 16:20", updateTime: "2026-04-09 16:20", agent: "待分配", description: "用户对比了近3个月账单，发现2月账单金额偏高，要求核查用气记录。", satisfaction: undefined, tags: ["账单", "核查"] },
  { id: "TK-2026-0889", title: "报装申请进度查询", type: "报装咨询", priority: "low", status: "resolved", channel: "钉钉", channelIcon: "ri-dingding-line", user: "陈小红", userId: "R004", phone: "136****5432", createTime: "2026-04-09 14:10", updateTime: "2026-04-10 09:45", agent: "客服02", description: "用户询问报装申请BZ-2026-004567的当前进度，已完成现场勘查，等待施工排期。", satisfaction: 5, tags: ["报装", "进度"] },
  { id: "TK-2026-0888", title: "燃气泄漏紧急报修", type: "紧急报修", priority: "urgent", status: "closed", channel: "电话", channelIcon: "ri-phone-line", user: "赵志远", userId: "R005", phone: "135****7890", createTime: "2026-04-08 22:30", updateTime: "2026-04-09 01:15", agent: "应急组", description: "用户深夜发现厨房有燃气味，已关闭阀门，紧急派遣抢修人员上门处理，已排除泄漏隐患。", satisfaction: 5, tags: ["紧急", "泄漏", "已处理"] },
  { id: "TK-2026-0887", title: "申请开具用气证明", type: "证明开具", priority: "low", status: "resolved", channel: "企业微信", channelIcon: "ri-wechat-2-line", user: "孙丽华", userId: "R006", phone: "132****4567", createTime: "2026-04-08 10:00", updateTime: "2026-04-08 15:30", agent: "客服03", description: "用户申请开具近一年用气量证明，用于房屋租赁合同。", satisfaction: 4, tags: ["证明", "开具"] },
  { id: "TK-2026-0886", title: "短信验证码无法接收", type: "账号问题", priority: "medium", status: "resolved", channel: "QQ", channelIcon: "ri-qq-line", user: "刘建平", userId: "R007", phone: "188****3456", createTime: "2026-04-07 14:25", updateTime: "2026-04-07 16:00", agent: "客服01", description: "用户更换手机号后无法接收验证码，无法登录APP，已协助更新手机号信息。", satisfaction: 4, tags: ["账号", "验证码"] },
  { id: "TK-2026-0885", title: "申请安检预约", type: "安检预约", priority: "medium", status: "closed", channel: "短信", channelIcon: "ri-message-3-line", user: "周明亮", userId: "R008", phone: "177****8901", createTime: "2026-04-07 09:30", updateTime: "2026-04-08 10:00", agent: "客服02", description: "用户申请年度安检预约，已安排2026-04-15上午9:00-11:00上门安检。", satisfaction: 5, tags: ["安检", "预约"] },
];

const channels: Channel[] = [
  { id: "wechat", name: "微信公众号", icon: "ri-wechat-line", color: "text-green-600", bgColor: "bg-green-50", status: "online", todayCount: 18, avgResponse: "1.2分钟", connected: true },
  { id: "sms", name: "短信服务", icon: "ri-message-3-line", color: "text-sky-600", bgColor: "bg-sky-50", status: "online", todayCount: 12, avgResponse: "自动回复", connected: true },
  { id: "app", name: "APP消息", icon: "ri-smartphone-line", color: "text-[#E8531A]", bgColor: "bg-orange-50", status: "online", todayCount: 24, avgResponse: "0.8分钟", connected: true },
  { id: "feishu", name: "飞书", icon: "ri-message-2-line", color: "text-cyan-600", bgColor: "bg-cyan-50", status: "online", todayCount: 6, avgResponse: "2.1分钟", connected: true },
  { id: "dingtalk", name: "钉钉", icon: "ri-dingding-line", color: "text-amber-600", bgColor: "bg-amber-50", status: "online", todayCount: 9, avgResponse: "1.8分钟", connected: true },
  { id: "qq", name: "QQ客服", icon: "ri-qq-line", color: "text-sky-500", bgColor: "bg-sky-50", status: "online", todayCount: 4, avgResponse: "3.2分钟", connected: true },
  { id: "wxwork", name: "企业微信", icon: "ri-wechat-2-line", color: "text-green-700", bgColor: "bg-green-50", status: "online", todayCount: 7, avgResponse: "1.5分钟", connected: true },
  { id: "phone", name: "电话热线", icon: "ri-phone-line", color: "text-gray-600", bgColor: "bg-gray-50", status: "online", todayCount: 47, avgResponse: "30秒", connected: true },
];

const satisfactionData = [
  { score: 5, count: 128, label: "非常满意", color: "bg-green-500" },
  { score: 4, count: 89, label: "满意", color: "bg-teal-500" },
  { score: 3, count: 23, label: "一般", color: "bg-amber-500" },
  { score: 2, count: 8, label: "不满意", color: "bg-orange-500" },
  { score: 1, count: 3, label: "非常不满意", color: "bg-red-500" },
];

const aiResponses: Record<string, string> = {
  "账单": "根据用户档案，该用户本月账单金额为 ¥89.34，截止日期2026-04-30，当前余额¥128.50，账单已覆盖，无需催缴。",
  "缴费": "用户支持微信支付、支付宝、银行代扣等方式缴费。当前已签约工商银行代扣，下次自动扣款日为2026-04-25。",
  "泄漏": "⚠️ 紧急情况！请立即告知用户：1.关闭总阀门 2.开窗通风 3.疏散人员 4.不要开关电器。同时派遣抢修人员，预计30分钟内到达。",
  "报装": "报装流程约需15个工作日，当前受理量较大，建议预约2026-04-20后的时间段。需要准备：房产证、身份证原件及复印件。",
  "安检": "该用户下次安检日期为2026-06-15，距今还有66天。可为用户预约安检时间，推荐工作日上午9:00-11:00时段。",
  "余额": "用户当前账户余额¥128.50，本月已用气28.5m³，预计本月费用约¥98.33，余额充足。",
};

const userInfo = {
  R001: { name: "张建国", address: "朝阳区望京街道1号楼3单元201", balance: 128.50, monthlyUsage: 28.5, meterNo: "IC-2024-001234", status: "正常", nextSafety: "2026-06-15" },
  R002: { name: "李秀梅", address: "海淀区中关村南大街15号", balance: 12.30, monthlyUsage: 35.2, meterNo: "IC-2023-005678", status: "欠费预警", nextSafety: "2026-04-15" },
  R003: { name: "王大明", address: "丰台区南四环西路128号3单元301", balance: 520.00, monthlyUsage: 42.8, meterNo: "IC-2024-009012", status: "正常", nextSafety: "2027-03-10" },
};

export default function ServicePage() {
  const [activeTab, setActiveTab] = useState<"calls" | "tickets" | "channels" | "satisfaction" | "analytics">("calls");
  const [selectedCall, setSelectedCall] = useState<Call | null>(mockCalls[0]);
  const [messages, setMessages] = useState<Message[]>([
    { role: "system", content: "AI客服助手已就绪，已加载用户 张建国 的完整档案信息", time: "14:22" },
    { role: "user", content: "你好，我想查询一下我的账单情况", time: "14:22" },
    { role: "ai", content: "您好！根据您的账户信息，您本月（2026年4月）账单金额为 ¥89.34，截止缴费日期为2026年4月30日。您当前账户余额为 ¥128.50，余额充足，无需担心。请问还有其他需要帮助的吗？", time: "14:22" },
  ]);
  const [inputMsg, setInputMsg] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [aiReport, setAiReport] = useState("");
  const [callTimer, setCallTimer] = useState(245);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketFilter, setTicketFilter] = useState<"all" | "open" | "processing" | "resolved">("all");
  const [ticketSearch, setTicketSearch] = useState("");
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({ title: "", type: "账单咨询", priority: "medium", channel: "电话", description: "" });
  const [ticketCreated, setTicketCreated] = useState(false);
  const [channelFilter, setChannelFilter] = useState<"all" | "online" | "offline">("all");
  const [satisfactionPeriod, setSatisfactionPeriod] = useState<"today" | "week" | "month">("month");
  const [monitoringCall, setMonitoringCall] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const satisfactionCanvasRef = useRef<HTMLCanvasElement>(null);
  const trendCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const timer = setInterval(() => setCallTimer(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Draw satisfaction donut
  useEffect(() => {
    if (activeTab !== "satisfaction") return;
    const canvas = satisfactionCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const cx = W / 2;
    const cy = H / 2;
    const r = Math.min(W, H) / 2 - 10;
    const total = satisfactionData.reduce((s, d) => s + d.count, 0);
    const colors = ["#22c55e", "#14b8a6", "#f59e0b", "#f97316", "#ef4444"];
    let startAngle = -Math.PI / 2;
    satisfactionData.forEach((d, i) => {
      const angle = (d.count / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, startAngle + angle);
      ctx.closePath();
      ctx.fillStyle = colors[i];
      ctx.fill();
      startAngle += angle;
    });
    // Inner circle (donut hole)
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.55, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();
    // Center text
    const avg = satisfactionData.reduce((s, d) => s + d.score * d.count, 0) / total;
    ctx.fillStyle = "#1f2937";
    ctx.font = "bold 18px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(avg.toFixed(1), cx, cy - 6);
    ctx.fillStyle = "#9ca3af";
    ctx.font = "10px sans-serif";
    ctx.fillText("综合评分", cx, cy + 10);
  }, [activeTab, satisfactionPeriod]);

  // Draw satisfaction trend
  useEffect(() => {
    if (activeTab !== "satisfaction") return;
    const canvas = trendCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const data = [4.5, 4.6, 4.7, 4.6, 4.8, 4.7, 4.8, 4.9, 4.8, 4.9, 4.8, 4.9];
    const labels = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
    const padL = 28, padR = 10, padT = 12, padB = 20;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const minV = 4.3;
    const maxV = 5.0;
    const range = maxV - minV;
    // Grid
    for (let i = 0; i <= 3; i++) {
      const y = padT + (chartH / 3) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.06)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const val = maxV - (range / 3) * i;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(val.toFixed(1), padL - 3, y + 3);
    }
    // Area fill
    const grad = ctx.createLinearGradient(0, padT, 0, H - padB);
    grad.addColorStop(0, "rgba(34,197,94,0.2)");
    grad.addColorStop(1, "rgba(34,197,94,0)");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + chartW, H - padB);
    ctx.lineTo(padL, H - padB);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
    // Line
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.stroke();
    // Dots
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = "#22c55e";
      ctx.fill();
    });
    // Labels
    labels.forEach((l, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(l, x, H - 4);
    });
  }, [activeTab, satisfactionPeriod]);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const sendMessage = () => {
    if (!inputMsg.trim()) return;
    const userMsg: Message = { role: "user", content: inputMsg, time: new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }) };
    setMessages(prev => [...prev, userMsg]);
    const keyword = Object.keys(aiResponses).find(k => inputMsg.includes(k));
    const aiReply = keyword ? aiResponses[keyword] : "我已记录您的问题，正在为您查询相关信息，请稍候...";
    setTimeout(() => {
      setMessages(prev => [...prev, { role: "ai", content: aiReply, time: new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }) }]);
    }, 800);
    setInputMsg("");
  };

  const generateReport = () => {
    setReportGenerating(true);
    setAiReport("");
    const text = `【AI客服通话分析报告】
通话编号：CALL-001 | 时间：2026-04-10 14:22 | 时长：${formatDuration(callTimer)}

一、通话概况
• 来电用户：张建国（R001）
• 问题类型：账单咨询
• 情绪分析：中性偏正面，用户态度友好
• 解决状态：已解决

二、AI智能分析
1. 用户意图识别：账单查询（置信度98.2%）
2. 关键词提取：账单、余额、缴费日期
3. 情绪变化：通话全程保持平稳，无负面情绪
4. 解决效率：首次接触解决率100%

三、服务质量评估
• 响应速度：优秀（<30秒接入）
• 问题解决率：100%
• 预计用户满意度：4.8/5.0

四、AI建议
1. 为该用户开通账单自动推送服务
2. 推荐签约银行代扣，减少手动缴费
3. 下次安检（2026-06-15）提前15天发送提醒`;
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) { setAiReport(prev => prev + text[i]); i++; }
      else { clearInterval(interval); setReportGenerating(false); }
    }, 20);
  };

  const handleCreateTicket = () => {
    if (!newTicketForm.title || !newTicketForm.description) return;
    setTicketCreated(true);
    setTimeout(() => { setTicketCreated(false); setShowNewTicket(false); setNewTicketForm({ title: "", type: "账单咨询", priority: "medium", channel: "电话", description: "" }); }, 2000);
  };

  const filteredTickets = mockTickets.filter(t => {
    const matchFilter = ticketFilter === "all" || t.status === ticketFilter || (ticketFilter === "resolved" && (t.status === "resolved" || t.status === "closed"));
    const matchSearch = !ticketSearch || t.title.includes(ticketSearch) || t.user.includes(ticketSearch) || t.id.includes(ticketSearch);
    return matchFilter && matchSearch;
  });

  const priorityConfig = {
    urgent: { label: "紧急", color: "text-red-600", bg: "bg-red-50", dot: "bg-red-500" },
    high: { label: "高", color: "text-orange-600", bg: "bg-orange-50", dot: "bg-orange-500" },
    medium: { label: "中", color: "text-amber-600", bg: "bg-amber-50", dot: "bg-amber-500" },
    low: { label: "低", color: "text-gray-500", bg: "bg-gray-50", dot: "bg-gray-400" },
  };

  const statusConfig = {
    open: { label: "待处理", color: "text-red-600", bg: "bg-red-50" },
    processing: { label: "处理中", color: "text-amber-600", bg: "bg-amber-50" },
    pending: { label: "待分配", color: "text-gray-600", bg: "bg-gray-50" },
    resolved: { label: "已解决", color: "text-green-600", bg: "bg-green-50" },
    closed: { label: "已关闭", color: "text-gray-400", bg: "bg-gray-50" },
  };

  const callStatusConfig: Record<string, { color: string; bg: string; label: string; dot: string }> = {
    active: { color: "text-green-700", bg: "bg-green-50", label: "通话中", dot: "bg-green-500 animate-pulse" },
    waiting: { color: "text-amber-700", bg: "bg-amber-50", label: "等待中", dot: "bg-amber-500 animate-pulse" },
    ended: { color: "text-gray-600", bg: "bg-gray-50", label: "已结束", dot: "bg-gray-400" },
    missed: { color: "text-red-600", bg: "bg-red-50", label: "未接", dot: "bg-red-500" },
  };

  const currentUser = selectedCall?.userId && selectedCall.userId !== "-"
    ? userInfo[selectedCall.userId as keyof typeof userInfo]
    : null;

  const totalSatisfaction = satisfactionData.reduce((s, d) => s + d.count, 0);
  const avgScore = satisfactionData.reduce((s, d) => s + d.score * d.count, 0) / totalSatisfaction;

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">AI客户服务中心</h1>
            <p className="text-sm text-gray-500 mt-0.5">智能呼叫 · 工单管理 · 多渠道接入 · 满意度评价</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span>系统在线</span>
            </div>
            <div className="flex items-center gap-2 bg-orange-50 text-[#E8531A] px-3 py-2 rounded-lg text-sm font-medium">
              <i className="ri-phone-line"></i>
              <span>今日来电 47 通</span>
            </div>
            <button onClick={() => setShowNewTicket(true)} className="flex items-center gap-1.5 bg-[#E8531A] text-white px-3 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
              <i className="ri-add-line"></i>新建工单
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-6 gap-3">
          {[
            { label: "今日来电", value: "47", sub: "+12%", color: "text-gray-800", icon: "ri-phone-line", bg: "bg-gray-50" },
            { label: "通话中", value: "3", sub: "实时监控", color: "text-green-600", icon: "ri-phone-fill", bg: "bg-green-50" },
            { label: "待处理工单", value: "8", sub: "需及时处理", color: "text-red-600", icon: "ri-file-warning-line", bg: "bg-red-50" },
            { label: "今日工单", value: "23", sub: "较昨日+3", color: "text-amber-600", icon: "ri-file-list-3-line", bg: "bg-amber-50" },
            { label: "首次解决率", value: "94.3%", sub: "AI辅助提升8%", color: "text-[#E8531A]", icon: "ri-check-double-line", bg: "bg-orange-50" },
            { label: "满意度", value: avgScore.toFixed(1), sub: "本月平均评分", color: "text-green-600", icon: "ri-star-line", bg: "bg-green-50" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                  <i className={`${s.icon} ${s.color} text-base`}></i>
                </div>
              </div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Main tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {[
            { key: "calls", label: "呼叫中心", icon: "ri-phone-line" },
            { key: "tickets", label: "工单管理", icon: "ri-file-list-3-line" },
            { key: "channels", label: "多渠道接入", icon: "ri-links-line" },
            { key: "satisfaction", label: "满意度评价", icon: "ri-star-line" },
            { key: "analytics", label: "数据分析", icon: "ri-bar-chart-line" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as typeof activeTab)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className={t.icon}></i>
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== CALLS TAB ===== */}
        {activeTab === "calls" && (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3 space-y-2">
              <div className="bg-white rounded-xl border border-gray-100 p-3">
                <div className="text-xs font-semibold text-gray-600 mb-2">通话队列</div>
                <div className="space-y-2">
                  {mockCalls.map(call => {
                    const cfg = callStatusConfig[call.status];
                    return (
                      <div key={call.id} onClick={() => setSelectedCall(call)} className={`p-3 rounded-xl cursor-pointer transition-all border ${selectedCall?.id === call.id ? "border-[#E8531A]/30 bg-orange-50" : "border-gray-100 hover:border-gray-200 bg-gray-50"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></div>
                            <span className="text-xs font-semibold text-gray-800">{call.caller}</span>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                        </div>
                        <div className="text-[10px] text-gray-400">{call.type}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-gray-400">{call.startTime}</span>
                          {call.status === "active" && <span className="text-[10px] font-mono text-green-600">{formatDuration(callTimer)}</span>}
                        </div>
                        {call.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {call.tags.map((tag, i) => (
                              <span key={i} className={`text-[9px] px-1 py-0.5 rounded ${tag === "紧急" ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}`}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-span-6 space-y-3">
              {selectedCall && (
                <>
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#E8531A] flex items-center justify-center">
                          <span className="text-white text-lg font-bold">{selectedCall.caller[0]}</span>
                        </div>
                        <div>
                          <div className="text-base font-bold text-gray-900">{selectedCall.caller}</div>
                          <div className="text-sm text-gray-500">{selectedCall.phone} · {selectedCall.type}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${callStatusConfig[selectedCall.status].dot}`}></div>
                            <span className={`text-xs font-medium ${callStatusConfig[selectedCall.status].color}`}>{callStatusConfig[selectedCall.status].label}</span>
                            {selectedCall.status === "active" && <span className="text-xs font-mono text-green-600">{formatDuration(callTimer)}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {selectedCall.status === "active" && (
                          <>
                            <button onClick={() => setMonitoringCall(monitoringCall === selectedCall.id ? null : selectedCall.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${monitoringCall === selectedCall.id ? "bg-[#E8531A] text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                              <i className="ri-headphone-line"></i>{monitoringCall === selectedCall.id ? "监听中" : "监听"}
                            </button>
                            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                              <i className="ri-record-circle-line text-red-500"></i>录音中
                            </button>
                          </>
                        )}
                        <button onClick={generateReport} className="flex items-center gap-1.5 bg-[#E8531A] text-white px-3 py-2 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                          <i className="ri-robot-2-line"></i>AI分析
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#E8531A] flex items-center justify-center">
                          <i className="ri-robot-2-line text-white text-xs"></i>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">AI智能辅助</span>
                      </div>
                      <button onClick={() => setIsListening(!isListening)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${isListening ? "bg-red-100 text-red-600" : "bg-white text-gray-600 border border-gray-200"}`}>
                        <i className={`${isListening ? "ri-mic-fill animate-pulse" : "ri-mic-line"}`}></i>
                        {isListening ? "语音识别中" : "开启语音"}
                      </button>
                    </div>
                    <div className="h-56 overflow-y-auto p-4 space-y-3">
                      {messages.map((msg, i) => (
                        <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                          {msg.role === "system" ? (
                            <div className="w-full text-center">
                              <span className="text-[10px] text-gray-400 bg-gray-50 px-3 py-1 rounded-full">{msg.content}</span>
                            </div>
                          ) : (
                            <>
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === "ai" ? "bg-[#E8531A]" : "bg-gray-200"}`}>
                                {msg.role === "ai" ? <i className="ri-robot-2-line text-white text-xs"></i> : <span className="text-gray-600 text-xs font-bold">客</span>}
                              </div>
                              <div className={`max-w-xs rounded-xl px-3 py-2 ${msg.role === "ai" ? "bg-orange-50 text-gray-700" : "bg-gray-100 text-gray-700"}`}>
                                <p className="text-xs leading-relaxed">{msg.content}</p>
                                <span className="text-[10px] text-gray-400 mt-1 block">{msg.time}</span>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                    <div className="border-t border-gray-100 p-3 flex gap-2">
                      <input type="text" value={inputMsg} onChange={e => setInputMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder="输入问题，AI实时提供解答建议..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A]" />
                      <button onClick={sendMessage} className="bg-[#E8531A] text-white px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-[#d44a15] whitespace-nowrap"><i className="ri-send-plane-line"></i></button>
                    </div>
                  </div>

                  {aiReport && (
                    <div className="bg-white rounded-xl border border-gray-100 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-800">AI通话分析报告</h4>
                        <button className="text-xs text-[#E8531A] cursor-pointer whitespace-nowrap"><i className="ri-download-line mr-1"></i>导出PDF</button>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 max-h-40 overflow-y-auto">
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{aiReport}</pre>
                        {reportGenerating && <span className="inline-block w-1 h-3 bg-[#E8531A] animate-pulse ml-0.5"></span>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="col-span-3 space-y-3">
              {currentUser && (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
                    <i className="ri-user-3-line text-[#E8531A]"></i>用户档案
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: "姓名", value: currentUser.name },
                      { label: "地址", value: currentUser.address },
                      { label: "余额", value: `¥${currentUser.balance.toFixed(2)}`, highlight: currentUser.balance < 50 },
                      { label: "本月用气", value: `${currentUser.monthlyUsage} m³` },
                      { label: "状态", value: currentUser.status, highlight: currentUser.status !== "正常" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start justify-between gap-2">
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{item.label}</span>
                        <span className={`text-[10px] font-medium text-right ${item.highlight ? "text-red-600" : "text-gray-700"}`}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
                  <i className="ri-record-circle-line text-red-500"></i>录音回放
                </div>
                <div className="space-y-2">
                  {mockCalls.filter(c => c.status === "ended").map(call => (
                    <div key={call.id} className="bg-gray-50 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-medium text-gray-700">{call.caller}</span>
                        <span className="text-[10px] text-gray-400">{formatDuration(call.duration)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="w-6 h-6 rounded-full bg-[#E8531A] flex items-center justify-center cursor-pointer flex-shrink-0">
                          <i className="ri-play-fill text-white text-xs"></i>
                        </button>
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div className="bg-[#E8531A] h-1.5 rounded-full w-0"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== TICKETS TAB ===== */}
        {activeTab === "tickets" && (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4 space-y-3">
              {/* Filters */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input type="text" placeholder="搜索工单..." value={ticketSearch} onChange={e => setTicketSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {[
                  { key: "all", label: "全部" },
                  { key: "open", label: "待处理" },
                  { key: "processing", label: "处理中" },
                  { key: "resolved", label: "已解决" },
                ].map(f => (
                  <button key={f.key} onClick={() => setTicketFilter(f.key as typeof ticketFilter)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${ticketFilter === f.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}>{f.label}</button>
                ))}
              </div>
              {/* Ticket list */}
              <div className="space-y-2">
                {filteredTickets.map(ticket => {
                  const pri = priorityConfig[ticket.priority];
                  const sta = statusConfig[ticket.status];
                  return (
                    <div key={ticket.id} onClick={() => setSelectedTicket(ticket)} className={`bg-white rounded-xl border p-3 cursor-pointer transition-all ${selectedTicket?.id === ticket.id ? "border-[#E8531A]/40 bg-orange-50/30" : "border-gray-100 hover:border-gray-200"}`}>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <div className={`w-1.5 h-1.5 rounded-full ${pri.dot}`}></div>
                          <span className={`text-[10px] font-medium ${pri.color}`}>{pri.label}</span>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${sta.bg} ${sta.color}`}>{sta.label}</span>
                      </div>
                      <div className="text-xs font-semibold text-gray-800 mb-1 line-clamp-2">{ticket.title}</div>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        <i className={`${ticket.channelIcon} text-xs`}></i>
                        <span>{ticket.channel}</span>
                        <span>·</span>
                        <span>{ticket.user}</span>
                        <span>·</span>
                        <span>{ticket.createTime.split(" ")[0]}</span>
                      </div>
                      {ticket.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {ticket.tags.map((tag, i) => (
                            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-span-8">
              {selectedTicket ? (
                <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
                  {/* Ticket header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400">{selectedTicket.id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityConfig[selectedTicket.priority].bg} ${priorityConfig[selectedTicket.priority].color}`}>
                          {priorityConfig[selectedTicket.priority].label}优先级
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig[selectedTicket.status].bg} ${statusConfig[selectedTicket.status].color}`}>
                          {statusConfig[selectedTicket.status].label}
                        </span>
                      </div>
                      <h2 className="text-base font-bold text-gray-900 mb-1">{selectedTicket.title}</h2>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span><i className={`${selectedTicket.channelIcon} mr-1`}></i>{selectedTicket.channel}</span>
                        <span><i className="ri-user-line mr-1"></i>{selectedTicket.user}</span>
                        <span><i className="ri-time-line mr-1"></i>{selectedTicket.createTime}</span>
                        <span><i className="ri-customer-service-2-line mr-1"></i>{selectedTicket.agent}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                        <i className="ri-user-add-line"></i>分配
                      </button>
                      <button className="flex items-center gap-1.5 bg-[#E8531A] text-white px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                        <i className="ri-check-line"></i>标记解决
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs font-semibold text-gray-600 mb-2">问题描述</div>
                    <p className="text-sm text-gray-700 leading-relaxed">{selectedTicket.description}</p>
                  </div>

                  {/* AI Analysis */}
                  <div className="bg-orange-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-robot-2-line text-[#E8531A]"></i>
                      <span className="text-xs font-semibold text-[#E8531A]">AI智能分析</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      {[
                        { label: "问题分类", value: selectedTicket.type, icon: "ri-price-tag-3-line" },
                        { label: "预计处理时长", value: selectedTicket.priority === "urgent" ? "2小时内" : selectedTicket.priority === "high" ? "4小时内" : "1个工作日", icon: "ri-time-line" },
                        { label: "相似工单", value: "3个", icon: "ri-file-copy-line" },
                      ].map((item, i) => (
                        <div key={i} className="bg-white rounded-lg p-2.5">
                          <div className="flex items-center gap-1.5 mb-1">
                            <i className={`${item.icon} text-[#E8531A] text-xs`}></i>
                            <span className="text-[10px] text-gray-400">{item.label}</span>
                          </div>
                          <div className="text-xs font-semibold text-gray-800">{item.value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      <strong className="text-[#E8531A]">AI建议：</strong>
                      {selectedTicket.type === "设备问题" && "建议安排专业技术人员上门检查，携带计量检测设备。参考历史案例，此类问题通常由表计老化或安装问题导致，处理时长约2-3小时。"}
                      {selectedTicket.type === "账单争议" && "建议调取该用户近3个月用气记录进行核查，重点检查2月份是否有异常用气时段。可通过系统直接导出用气明细发送给用户。"}
                      {selectedTicket.type === "维修申请" && "软管更换属于常规维护项目，建议安排最近的维修人员上门，预计30分钟内可完成更换。"}
                      {selectedTicket.type === "报装咨询" && "用户报装进度查询，可直接在系统中查看BZ-2026-004567的当前状态并告知用户。"}
                      {selectedTicket.type === "紧急报修" && "紧急泄漏已处理完毕，建议后续跟进用户满意度，并提供安全用气指导手册。"}
                      {selectedTicket.type === "证明开具" && "用气证明开具属于标准业务，可直接在系统中生成并通过邮件发送给用户。"}
                      {selectedTicket.type === "账号问题" && "手机号更新已完成，建议提醒用户及时更新其他绑定信息，并开启双重验证。"}
                      {selectedTicket.type === "安检预约" && "安检预约已完成，系统将在预约前24小时自动发送提醒短信给用户。"}
                    </div>
                  </div>

                  {/* Satisfaction */}
                  {selectedTicket.satisfaction && (
                    <div className="bg-green-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-star-line text-green-600"></i>
                        <span className="text-xs font-semibold text-green-700">用户满意度评价</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <i key={s} className={s <= selectedTicket.satisfaction! ? "ri-star-fill text-amber-400 text-lg" : "ri-star-line text-amber-400 text-lg"}></i>
                        ))}
                        <span className="text-sm font-bold text-gray-800 ml-1">{selectedTicket.satisfaction}.0</span>
                        <span className="text-xs text-gray-500">/ 5.0</span>
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-3">处理进度</div>
                    <div className="relative">
                      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-100"></div>
                      {[
                        { time: selectedTicket.createTime, action: "工单创建", desc: `通过${selectedTicket.channel}提交`, done: true },
                        { time: selectedTicket.updateTime, action: "工单分配", desc: `分配给 ${selectedTicket.agent}`, done: selectedTicket.agent !== "待分配" },
                        { time: selectedTicket.status === "processing" ? "处理中" : selectedTicket.status === "resolved" || selectedTicket.status === "closed" ? selectedTicket.updateTime : "待处理", action: "开始处理", desc: "客服人员介入处理", done: selectedTicket.status === "processing" || selectedTicket.status === "resolved" || selectedTicket.status === "closed" },
                        { time: selectedTicket.status === "resolved" || selectedTicket.status === "closed" ? selectedTicket.updateTime : "待完成", action: "问题解决", desc: "用户确认问题已解决", done: selectedTicket.status === "resolved" || selectedTicket.status === "closed" },
                      ].map((step, i) => (
                        <div key={i} className="relative flex gap-4 mb-3 last:mb-0">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${step.done ? "bg-[#E8531A]" : "bg-gray-200"}`}>
                            {step.done ? <i className="ri-check-line text-white text-xs"></i> : <div className="w-2 h-2 rounded-full bg-gray-400"></div>}
                          </div>
                          <div className="flex-1 pb-1">
                            <div className="flex items-center justify-between">
                              <div className="text-xs font-medium text-gray-800">{step.action}</div>
                              <div className="text-[10px] text-gray-400">{step.time}</div>
                            </div>
                            <div className="text-[10px] text-gray-500 mt-0.5">{step.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-8 text-center h-full flex flex-col items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-file-list-3-line text-gray-300 text-3xl"></i>
                  </div>
                  <p className="text-gray-400 text-sm">选择左侧工单查看详情</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== CHANNELS TAB ===== */}
        {activeTab === "channels" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {[
                  { key: "all", label: "全部渠道" },
                  { key: "online", label: "在线" },
                  { key: "offline", label: "离线" },
                ].map(f => (
                  <button key={f.key} onClick={() => setChannelFilter(f.key as typeof channelFilter)} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${channelFilter === f.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}>{f.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span>{channels.filter(c => c.status === "online").length} 个渠道在线</span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {channels.map(ch => (
                <div key={ch.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:border-[#E8531A]/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl ${ch.bgColor} flex items-center justify-center`}>
                      <i className={`${ch.icon} ${ch.color} text-xl`}></i>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${ch.status === "online" ? "bg-green-500 animate-pulse" : "bg-gray-400"}`}></div>
                      <span className={`text-xs font-medium ${ch.status === "online" ? "text-green-600" : "text-gray-400"}`}>
                        {ch.status === "online" ? "在线" : "离线"}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm font-bold text-gray-900 mb-1">{ch.name}</div>
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">今日消息</span>
                      <span className="font-semibold text-gray-700">{ch.todayCount} 条</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">平均响应</span>
                      <span className="font-semibold text-gray-700">{ch.avgResponse}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 bg-gray-50 text-gray-600 py-1.5 rounded-lg text-xs cursor-pointer hover:bg-gray-100 whitespace-nowrap">配置</button>
                    <button className={`flex-1 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap ${ch.connected ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-[#E8531A] text-white hover:bg-[#d44a15]"}`}>
                      {ch.connected ? "已连接" : "连接"}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Channel stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">各渠道今日消息量</h3>
                <div className="space-y-2.5">
                  {channels.sort((a, b) => b.todayCount - a.todayCount).map(ch => (
                    <div key={ch.id}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <i className={`${ch.icon} ${ch.color} text-sm`}></i>
                          <span className="text-xs text-gray-600">{ch.name}</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-700">{ch.todayCount}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-[#E8531A]" style={{ width: `${(ch.todayCount / 47) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">渠道接入配置说明</h3>
                <div className="space-y-3">
                  {[
                    { icon: "ri-wechat-line", name: "微信公众号", desc: "通过微信公众平台API接入，支持自动回复和人工转接", color: "text-green-600" },
                    { icon: "ri-message-2-line", name: "飞书/钉钉", desc: "企业协作平台接入，支持工单自动创建和团队协作", color: "text-cyan-600" },
                    { icon: "ri-qq-line", name: "QQ客服", desc: "腾讯企点接入，支持多坐席并发和智能路由", color: "text-sky-500" },
                    { icon: "ri-wechat-2-line", name: "企业微信", desc: "企业微信客服接入，与内部系统深度集成", color: "text-green-700" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <i className={`${item.icon} ${item.color} text-lg flex-shrink-0 mt-0.5`}></i>
                      <div>
                        <div className="text-xs font-semibold text-gray-800">{item.name}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== SATISFACTION TAB ===== */}
        {activeTab === "satisfaction" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {[
                  { key: "today", label: "今日" },
                  { key: "week", label: "本周" },
                  { key: "month", label: "本月" },
                ].map(p => (
                  <button key={p.key} onClick={() => setSatisfactionPeriod(p.key as typeof satisfactionPeriod)} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${satisfactionPeriod === p.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}>{p.label}</button>
                ))}
              </div>
              <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                <i className="ri-download-line"></i>导出报告
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Donut chart */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">满意度分布</h3>
                <div className="flex items-center gap-4">
                  <canvas ref={satisfactionCanvasRef} width={120} height={120} className="flex-shrink-0" />
                  <div className="space-y-2 flex-1">
                    {satisfactionData.map((d, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${d.color}`}></div>
                          <span className="text-xs text-gray-600">{d.label}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-gray-700">{d.count}</span>
                          <span className="text-[10px] text-gray-400">({((d.count / totalSatisfaction) * 100).toFixed(0)}%)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Trend chart */}
              <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">满意度趋势（近12个月）</h3>
                <canvas ref={trendCanvasRef} width={480} height={120} className="w-full" />
              </div>
            </div>

            {/* Satisfaction records */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">最新评价记录</h3>
              <div className="space-y-3">
                {mockTickets.filter(t => t.satisfaction).map(ticket => (
                  <div key={ticket.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-[#E8531A] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">{ticket.user[0]}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">{ticket.user}</span>
                          <span className="text-xs text-gray-400">{ticket.type}</span>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <i key={s} className={s <= ticket.satisfaction! ? "ri-star-fill text-amber-400 text-sm" : "ri-star-line text-amber-400 text-sm"}></i>
                            ))}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{ticket.updateTime}</span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {ticket.satisfaction === 5 ? "服务非常专业，问题解决得很及时，非常满意！" :
                          ticket.satisfaction === 4 ? "整体服务不错，处理速度较快，基本满意。" :
                            "服务一般，希望能更快速地响应。"}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <i className={`${ticket.channelIcon} text-gray-400 text-xs`}></i>
                        <span className="text-[10px] text-gray-400">来自 {ticket.channel}</span>
                        <span className="text-[10px] text-gray-400">· 工单 {ticket.id}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Satisfaction Analysis */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-robot-2-line text-[#E8531A]"></i>
                <h3 className="text-sm font-semibold text-gray-800">AI满意度分析</h3>
                <span className="ml-auto text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">本月综合评分 {avgScore.toFixed(1)}/5.0</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "好评率", value: "87.3%", trend: "+2.1%", icon: "ri-thumb-up-line", color: "text-green-600", bg: "bg-green-50" },
                  { label: "首次解决率", value: "94.3%", trend: "+3.5%", icon: "ri-check-double-line", color: "text-[#E8531A]", bg: "bg-orange-50" },
                  { label: "平均响应时长", value: "1.8分钟", trend: "-0.3分钟", icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50" },
                ].map((item, i) => (
                  <div key={i} className={`${item.bg} rounded-xl p-4`}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${item.icon} ${item.color} text-base`}></i>
                      <span className="text-xs text-gray-500">{item.label}</span>
                    </div>
                    <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
                    <div className="text-[10px] text-green-600 mt-0.5">{item.trend} 较上月</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-orange-50 rounded-xl p-4">
                <div className="text-xs font-semibold text-[#E8531A] mb-2">AI改进建议</div>
                <div className="space-y-1.5">
                  {[
                    "账单类问题满意度最高（4.9分），建议推广自助查询功能减少人工介入",
                    "紧急报修类响应时间偏长，建议优化派单流程，目标响应时间缩短至15分钟",
                    "QQ渠道满意度相对较低（4.2分），建议增加该渠道的AI自动回复覆盖率",
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <i className="ri-lightbulb-line text-[#E8531A] text-xs mt-0.5 flex-shrink-0"></i>
                      <p className="text-xs text-gray-600 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== ANALYTICS TAB ===== */}
        {activeTab === "analytics" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">今日来电量分布</h3>
              <div className="flex items-end gap-1 h-32">
                {[3, 5, 8, 12, 15, 18, 22, 19, 16, 14, 11, 8].map((v, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t bg-[#E8531A]/80 hover:bg-[#E8531A] transition-colors cursor-pointer" style={{ height: `${(v / 22) * 100}%` }} />
                    <span className="text-[8px] text-gray-400">{8 + i}时</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">工单类型分布</h3>
              <div className="space-y-2.5">
                {[
                  { label: "账单缴费", count: 18, pct: 38, color: "bg-[#E8531A]" },
                  { label: "设备维修", count: 12, pct: 26, color: "bg-amber-500" },
                  { label: "报装咨询", count: 8, pct: 17, color: "bg-green-500" },
                  { label: "安检预约", count: 6, pct: 13, color: "bg-cyan-500" },
                  { label: "其他", count: 3, pct: 6, color: "bg-gray-400" },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="text-gray-500">{item.count}次 ({item.pct}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`h-2 rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">渠道来源分布</h3>
              <div className="space-y-2.5">
                {channels.sort((a, b) => b.todayCount - a.todayCount).slice(0, 5).map(ch => (
                  <div key={ch.id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <i className={`${ch.icon} ${ch.color} text-sm`}></i>
                        <span className="text-xs text-gray-600">{ch.name}</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{ch.todayCount}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-[#E8531A]" style={{ width: `${(ch.todayCount / 47) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">AI辅助效果分析</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "AI建议采纳率", value: "87.3%", color: "text-[#E8531A]" },
                  { label: "平均处理时长", value: "3.2分钟", color: "text-green-600" },
                  { label: "首次解决率", value: "94.3%", color: "text-amber-600" },
                  { label: "知识库命中率", value: "91.5%", color: "text-cyan-600" },
                ].map((s, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowNewTicket(false)}>
          <div className="bg-white rounded-2xl p-6 w-[520px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">新建工单</h3>
              <button onClick={() => setShowNewTicket(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            {ticketCreated ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-3xl"></i>
                </div>
                <div className="text-base font-bold text-gray-900">工单创建成功！</div>
                <div className="text-xs text-gray-400 mt-1">工单编号：TK-2026-0893</div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">工单标题 <span className="text-red-400">*</span></label>
                  <input type="text" value={newTicketForm.title} onChange={e => setNewTicketForm(p => ({ ...p, title: e.target.value }))} placeholder="请输入工单标题" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">工单类型</label>
                    <select value={newTicketForm.type} onChange={e => setNewTicketForm(p => ({ ...p, type: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#E8531A]">
                      {["账单咨询", "设备问题", "维修申请", "报装咨询", "安检预约", "账号问题", "紧急报修", "其他"].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">优先级</label>
                    <select value={newTicketForm.priority} onChange={e => setNewTicketForm(p => ({ ...p, priority: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#E8531A]">
                      <option value="urgent">紧急</option>
                      <option value="high">高</option>
                      <option value="medium">中</option>
                      <option value="low">低</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">接入渠道</label>
                  <div className="grid grid-cols-4 gap-2">
                    {channels.map(ch => (
                      <button key={ch.id} onClick={() => setNewTicketForm(p => ({ ...p, channel: ch.name }))} className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-colors cursor-pointer ${newTicketForm.channel === ch.name ? "border-[#E8531A] bg-orange-50" : "border-gray-100"}`}>
                        <i className={`${ch.icon} ${ch.color} text-lg`}></i>
                        <span className="text-[9px] text-gray-600 text-center leading-tight">{ch.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">问题描述 <span className="text-red-400">*</span></label>
                  <textarea rows={4} maxLength={500} value={newTicketForm.description} onChange={e => setNewTicketForm(p => ({ ...p, description: e.target.value }))} placeholder="请详细描述用户问题..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#E8531A] resize-none" />
                  <div className="text-right text-[10px] text-gray-300 mt-0.5">{newTicketForm.description.length}/500</div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowNewTicket(false)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap">取消</button>
                  <button onClick={handleCreateTicket} disabled={!newTicketForm.title || !newTicketForm.description} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap">创建工单</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </MainLayout>
  );
}
